/* Hallmark · component: settings-nav · genre: modern-minimal · theme: project system
 * states: default · hover · focus · active(aria-current) — disabled/loading/error/success n/a for nav
 * pre-emit critique: P4 H5 E4 S5 R5 V4
 */

/**
 * SettingsNav — the settings navigation, responsive in shape:
 *
 *   - lg+   → a vertical inner sidebar (the Stripe/GitHub settings pattern).
 *             Scales with future entries and gives the pages the full content
 *             width — the same reason Roles and Gov-Credentials were once
 *             evacuated OUT of the horizontal tabs (changelog 2026-06-15).
 *   - < lg  → horizontal tabs in an overflow-x-auto strip. Core's SettingsTabs
 *             had no scroll container at all, so on phones the five tabs just
 *             squeezed; here they actually pan sideways.
 *
 * Replaces core-frontend's <SettingsTabs /> for THIS app only (core's
 * component stays for the sibling apps). The five base entries come from
 * core's exported `baseSettingsTabs` (3.37.0+) — `useSettingsTabs()` only
 * returns plugin-registered tabs.
 *
 * Zwei Seiten (Plan API 2026-09-25 „Mein Konto und System"): `section`
 * waehlt die Reiter — `'account'` das Persoenliche unter `/settings` (Avatar →
 * „Mein Konto"), `'system'` das der Organisation unter `/system`
 * (Seitenleiste → Organisation → System). Die Zuordnung traegt jeder Reiter
 * selbst (`SettingsTab.section`, ohne Angabe Konto).
 */

import {
  baseSettingsTabs,
  filterSettingsTabsBySection,
  Icon,
  type SettingsTab,
  type SettingsTabSection,
  useSettingsTabs,
} from '@xhubio-saas/core-frontend/kernel'
import { cn } from '@xhubio-saas/core-frontend/ui-components'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { CONTROL_TRANSITION, FIELD_RING } from '../pages/auth/authUi'

/**
 * Der Kernel-Basisreiter „System" zeigt auf `/settings/system`; in dieser App
 * ist die System-Seite `/system` (die alte Adresse leitet weiter). Plugin- und
 * App-Reiter tragen ihr Ziel selbst, nur der Basisreiter braucht die Umlenkung.
 */
const BASE_TAB_HREF: Record<string, string> = { system: '/system' }

export function SettingsNav({ section = 'account' }: { section?: SettingsTabSection }) {
  const { t } = useTranslation()
  const location = useLocation()
  // Plugin-registered tabs join in (entitlement-filtered by the hook).
  const { tabs: pluginTabs } = useSettingsTabs()

  const allTabs = filterSettingsTabsBySection([...baseSettingsTabs, ...pluginTabs], section)
    .map((tab) => ({ ...tab, href: BASE_TAB_HREF[tab.id] ?? tab.href }))
    .sort((a, b) => a.order - b.order)

  // Same active rule as core: exact path match, plus bare /settings mapping to
  // the profile tab (the /settings route renders the profile page).
  const isActive = (tab: SettingsTab) =>
    location.pathname === tab.href ||
    (tab.href === '/settings/profile' && location.pathname === '/settings')

  const navLabel =
    section === 'system' ? t('settings.system.tab', 'System') : t('common.myAccount', 'My account')

  return (
    <>
      {/* < lg — horizontal tabs, finally pannable. No scroll-jumping logic on
          focus/selection; the strip just scrolls (slop-test gate 53). */}
      <nav
        aria-label={navLabel}
        data-testid="settings-nav-tabs"
        className="flex gap-1 overflow-x-auto border-b lg:hidden"
      >
        {allTabs.map((tab) => {
          const active = isActive(tab)
          return (
            <Link
              key={tab.id}
              to={tab.href}
              aria-current={active ? 'page' : undefined}
              data-testid={`settings-tab-${tab.id}`}
              className={cn(
                '-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium',
                CONTROL_TRANSITION,
                FIELD_RING,
                active
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-muted-foreground/25 hover:text-foreground'
              )}
            >
              {tab.icon && <Icon icon={tab.icon} className="h-4 w-4" />}
              {t(tab.labelKey, tab.id)}
            </Link>
          )
        })}
      </nav>

      {/* lg+ — the inner sidebar. Active entry is a quiet pill (bg-accent), not
          a coloured side stripe. */}
      <nav
        aria-label={navLabel}
        data-testid="settings-nav-sidebar"
        className="hidden flex-col gap-0.5 lg:flex"
      >
        {allTabs.map((tab) => {
          const active = isActive(tab)
          return (
            <Link
              key={tab.id}
              to={tab.href}
              aria-current={active ? 'page' : undefined}
              data-testid={`settings-side-${tab.id}`}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm',
                CONTROL_TRANSITION,
                FIELD_RING,
                active
                  ? 'bg-accent font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
              )}
            >
              {tab.icon && <Icon icon={tab.icon} className="h-4 w-4 shrink-0" />}
              <span className="truncate">{t(tab.labelKey, tab.id)}</span>
            </Link>
          )
        })}
      </nav>
    </>
  )
}
